import type { Uuid } from "spacetimedb";
import type { CanonicalAddress } from "@tycho/web-bindings/types";

/**
 * Pin/legend categories for the Explore map. A property's category drives its
 * pin color and its row in the legend. `selected` is not a real category — it's
 * the override color a pin takes while it's in the user's staged selection (red,
 * per Figma), regardless of its underlying category.
 */
export type ExplorePinCategory = "forLease" | "forSale" | "offMarket" | "operatingLocation";

/** Pin/legend colors per category, plus the selected-state override. */
export const EXPLORE_PIN_COLORS: Record<ExplorePinCategory | "selected", string> = {
  forLease: "var(--color-withus-green)",
  forSale: "var(--color-withus-blue)",
  offMarket: "#9ca3af", // gray-400
  operatingLocation: "var(--color-withus-yellow)",
  selected: "var(--color-withus-red)",
};

export const EXPLORE_PIN_LABELS: Record<ExplorePinCategory | "selected", string> = {
  forLease: "For lease",
  forSale: "For sale",
  offMarket: "Off market",
  operatingLocation: "Your location",
  selected: "Selected",
};

/** Legend order, top to bottom. */
export const EXPLORE_LEGEND_ORDER: (ExplorePinCategory | "selected")[] = [
  "forLease",
  "forSale",
  "offMarket",
  "operatingLocation",
  "selected",
];

/**
 * A property a user can target, unified across its three sources so the map,
 * the scroller, and the selection logic all speak one shape.
 *
 * `key` is the stable selection key (also the map pin id):
 *   - Catskills:           `catskills:<id>`        (matches `catskillsExternalId`)
 *   - operating location:  `op:<targetablePropertyId>`
 *   - searched address:    `mapbox:<mapboxId>`
 *
 * The discriminated `source` carries exactly what `handleConfirm` needs to add
 * + target the property:
 *   - `catskills` rows are added to the catalog by `externalId` then targeted.
 *   - `operating` rows are already in the catalog (confirming an operating
 *     location creates the targetable-property row), so they're targeted by id.
 *   - `searched` rows are added to the catalog as MANUAL by `externalId`
 *     (`gmaps:<placeId>`) then targeted.
 */
export type ExplorePin = {
  key: string;
  category: ExplorePinCategory;
  latitude: number;
  longitude: number;
  address: CanonicalAddress;
  /** Ready-to-render URL when the catalog stores one (e.g. Catskills Street View). */
  imageUrl?: string;
  /**
   * Google Places identifiers for client-side photo resolution when `imageUrl`
   * is absent — operating locations promoted from Athena only carry these.
   */
  googlePlaceId?: string;
  googlePlacePhotoName?: string;
  /** "$1.25M Last sale" style attribute, when known. */
  lastSalePrice?: number;
  askingPrice?: number;
  sqft?: number;
  source: ExplorePinSource;
};

type ExplorePinSource =
  | { kind: "catskills"; externalId: string; imageUrl?: string }
  | { kind: "operating"; targetablePropertyId: Uuid }
  | { kind: "searched"; externalId: string };

/** Selected-state pin color overrides the category color. */
export function explorePinColor(category: ExplorePinCategory, selected: boolean): string {
  return selected ? EXPLORE_PIN_COLORS.selected : EXPLORE_PIN_COLORS[category];
}
